
import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import type { YtInterval, YtSettingsResponse, CreditsUsageResponse } from '../../api/ytmp3'
import {
  getCreditsUsage,
  getYtSettings,
  postCreditTopUp,
  updateYtSettings,
} from '../../api/ytmp3'

const INTERVAL_OPTIONS: { value: YtInterval; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom (N days)' },
]

const BITRATE_OPTIONS = [128, 192, 256, 320]

const YtMp3SettingsPage: React.FC = () => {
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [settings, setSettings] = React.useState<YtSettingsResponse | null>(null)
  const [hasStoredKey, setHasStoredKey] = React.useState(false)
  const [creditsUsage, setCreditsUsage] = React.useState<CreditsUsageResponse | null>(null)
  const [topupStationId, setTopupStationId] = React.useState<number | ''>('')
  const [topupAmount, setTopupAmount] = React.useState('')
  const [topupNote, setTopupNote] = React.useState('')
  const [topupSaving, setTopupSaving] = React.useState(false)
  const [toast, setToast] = React.useState<string | null>(null)

  const fetchAll = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [settingsResponse, creditsResponse] = await Promise.all([getYtSettings(), getCreditsUsage()])
      const storedKey = settingsResponse.provider.rapidapi_key === '__REDACTED__'
      setHasStoredKey(storedKey)
      setSettings({
        ...settingsResponse,
        provider: {
          ...settingsResponse.provider,
          rapidapi_key: storedKey ? '' : settingsResponse.provider.rapidapi_key,
        },
        credits: {
          ...settingsResponse.credits,
          stations: settingsResponse.credits.stations.map((station) => ({
            ...station,
          })),
        },
      })
      setCreditsUsage(creditsResponse)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load YT-MP3 settings.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  const handleProviderChange = (field: keyof YtSettingsResponse['provider'], value: string | number) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            provider: {
              ...prev.provider,
              [field]: value,
            },
          }
        : prev,
    )
  }

  const handleCreditsDefaultChange = (
    field: keyof YtSettingsResponse['credits']['defaults'],
    value: string | number,
  ) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            credits: {
              ...prev.credits,
              defaults: {
                ...prev.credits.defaults,
                [field]: value,
              },
            },
          }
        : prev,
    )
  }

  const handleStationOverrideChange = (
    stationId: number,
    field: keyof Omit<YtSettingsResponse['credits']['stations'][number], 'station_id' | 'name' | 'uses_default'>,
    value: string | number,
  ) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            credits: {
              ...prev.credits,
              stations: prev.credits.stations.map((station) =>
                station.station_id === stationId
                  ? {
                      ...station,
                      [field]: value,
                      uses_default: false,
                    }
                  : station,
              ),
            },
          }
        : prev,
    )
  }

  const handleStationUseDefault = (stationId: number) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            credits: {
              ...prev.credits,
              stations: prev.credits.stations.map((station) =>
                station.station_id === stationId
                  ? {
                      ...station,
                      allowance: prev.credits.defaults.allowance,
                      interval: prev.credits.defaults.interval,
                      custom_days: prev.credits.defaults.custom_days,
                      uses_default: true,
                    }
                  : station,
              ),
            },
          }
        : prev,
    )
  }

  const handleFileLimitChange = (value: number) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            file_limits: {
              ...prev.file_limits,
              max_mb: value,
            },
          }
        : prev,
    )
  }

  const handleAccessToggle = (checked: boolean) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            access: {
              ...prev.access,
              superadmin_only: checked,
            },
          }
        : prev,
    )
  }

  const handleSave = async () => {
    if (!settings) {
      return
    }

    setSaving(true)
    setError(null)

    const providerKey = settings.provider.rapidapi_key.trim()
    const payload: YtSettingsResponse = {
      ...settings,
      provider: {
        ...settings.provider,
        rapidapi_key: providerKey === '' ? (hasStoredKey ? '__UNCHANGED__' : '') : providerKey,
      },
      credits: {
        ...settings.credits,
        stations: settings.credits.stations
          .filter((station) => !station.uses_default)
          .map((station) => ({
            station_id: station.station_id,
            allowance: station.allowance,
            interval: station.interval,
            custom_days: station.custom_days,
          })) as YtSettingsResponse['credits']['stations'],
      },
    }

    try {
      const response = await updateYtSettings(payload)
      const updated = response.settings
      const storedKey = updated.provider.rapidapi_key === '__REDACTED__'
      setHasStoredKey(storedKey)
      setSettings({
        ...updated,
        provider: {
          ...updated.provider,
          rapidapi_key: storedKey ? '' : updated.provider.rapidapi_key,
        },
      })
      setToast('Settings saved successfully.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save YT-MP3 settings.'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleTopUp = async () => {
    if (!topupStationId || !topupAmount) {
      setToast('Select a station and amount for the top-up.')
      return
    }

    setTopupSaving(true)
    setToast(null)

    try {
      await postCreditTopUp({
        station_id: Number(topupStationId),
        amount: Number(topupAmount),
        note: topupNote.trim(),
      })
      setTopupAmount('')
      setTopupNote('')
      await fetchAll()
      setToast('Top-up applied.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to apply top-up.'
      setError(message)
    } finally {
      setTopupSaving(false)
    }
  }

  if (loading || !settings) {
    return (
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
        {error ? (
          <Stack spacing={2}>
            <Alert severity="error">{error}</Alert>
            <Button variant="contained" startIcon={<RefreshRoundedIcon />} onClick={() => fetchAll()}>
              Retry
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography>Loading YT-MP3 settings…</Typography>
          </Stack>
        )}
      </Paper>
    )
  }

  return (
    <Stack spacing={3}>
      {toast && <Alert severity="success">{toast}</Alert>}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Typography variant="h6">Provider & Retention</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="RapidAPI username"
              value={settings.provider.rapidapi_username}
              onChange={(event) => handleProviderChange('rapidapi_username', event.target.value)}
              fullWidth
            />
            <TextField
              label="RapidAPI key"
              type="password"
              placeholder={hasStoredKey ? '•••••••• (stored)' : ''}
              value={settings.provider.rapidapi_key}
              onChange={(event) => {
                setHasStoredKey(false)
                handleProviderChange('rapidapi_key', event.target.value)
              }}
              fullWidth
              helperText={hasStoredKey ? 'Leave blank to keep the stored secret.' : undefined}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="default-bitrate-label">Default bitrate</InputLabel>
              <Select
                labelId="default-bitrate-label"
                label="Default bitrate"
                value={settings.provider.default_bitrate}
                onChange={(event) => handleProviderChange('default_bitrate', Number(event.target.value))}
              >
                {BITRATE_OPTIONS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value} kbps
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Retention (days)"
              type="number"
              inputProps={{ min: 0 }}
              value={settings.provider.retention_days}
              onChange={(event) => handleProviderChange('retention_days', Number(event.target.value))}
              fullWidth
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Credits & per-station limits</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Default allowance"
              type="number"
              inputProps={{ min: 0 }}
              value={settings.credits.defaults.allowance}
              onChange={(event) => handleCreditsDefaultChange('allowance', Number(event.target.value))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="default-interval-label">Interval</InputLabel>
              <Select
                labelId="default-interval-label"
                label="Interval"
                value={settings.credits.defaults.interval}
                onChange={(event) => handleCreditsDefaultChange('interval', event.target.value as YtInterval)}
              >
                {INTERVAL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Custom days"
              type="number"
              inputProps={{ min: 1 }}
              disabled={settings.credits.defaults.interval !== 'custom'}
              value={settings.credits.defaults.custom_days}
              onChange={(event) => handleCreditsDefaultChange('custom_days', Number(event.target.value))}
              fullWidth
            />
          </Stack>
          <Divider />
          <Stack spacing={1}>
            <Typography variant="subtitle1">Per-station overrides</Typography>
            <Grid container spacing={2}>
              {settings.credits.stations.map((station) => (
                <React.Fragment key={station.station_id}>
                  <Grid item xs={12} md={3}>
                    <Typography fontWeight={600}>{station.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Station #{station.station_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Allowance"
                      type="number"
                      inputProps={{ min: 0 }}
                      value={station.allowance}
                      disabled={station.uses_default}
                      onChange={(event) =>
                        handleStationOverrideChange(station.station_id, 'allowance', Number(event.target.value))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth disabled={station.uses_default}>
                      <InputLabel id={`interval-${station.station_id}`}>Interval</InputLabel>
                      <Select
                        labelId={`interval-${station.station_id}`}
                        label="Interval"
                        value={station.interval}
                        onChange={(event) =>
                          handleStationOverrideChange(station.station_id, 'interval', event.target.value as YtInterval)
                        }
                      >
                        {INTERVAL_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Custom days"
                      type="number"
                      inputProps={{ min: 1 }}
                      disabled={station.uses_default || station.interval !== 'custom'}
                      value={station.custom_days}
                      onChange={(event) =>
                        handleStationOverrideChange(station.station_id, 'custom_days', Number(event.target.value))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button variant="text" onClick={() => handleStationUseDefault(station.station_id)}>
                      Use defaults
                    </Button>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Global quotas & file size</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Max conversions per day"
              type="number"
              inputProps={{ min: 0 }}
              value={settings.quotas.max_per_day}
              onChange={(event) =>
                setSettings((prev) =>
                  prev
                    ? {
                        ...prev,
                        quotas: { ...prev.quotas, max_per_day: Number(event.target.value) },
                      }
                    : prev,
                )
              }
              fullWidth
            />
            <TextField
              label="Max requests per second"
              type="number"
              inputProps={{ min: 1 }}
              value={settings.quotas.max_per_second}
              onChange={(event) =>
                setSettings((prev) =>
                  prev
                    ? {
                        ...prev,
                        quotas: { ...prev.quotas, max_per_second: Number(event.target.value) },
                      }
                    : prev,
                )
              }
              fullWidth
            />
            <TextField
              label="Warning threshold (%)"
              type="number"
              inputProps={{ min: 1, max: 100 }}
              value={settings.quotas.warning_threshold}
              onChange={(event) =>
                setSettings((prev) =>
                  prev
                    ? {
                        ...prev,
                        quotas: { ...prev.quotas, warning_threshold: Number(event.target.value) },
                      }
                    : prev,
                )
              }
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Max file size (MB)"
              type="number"
              inputProps={{ min: 1, max: 50 }}
              value={settings.file_limits.max_mb}
              onChange={(event) => handleFileLimitChange(Number(event.target.value))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.access.superadmin_only}
                  onChange={(_, checked) => handleAccessToggle(checked)}
                />
              }
              label="Restrict Tools menu to SuperAdmins"
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Credits top-up</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="topup-station-label">Station</InputLabel>
              <Select
                labelId="topup-station-label"
                label="Station"
                value={topupStationId}
                onChange={(event) => setTopupStationId(event.target.value === '' ? '' : Number(event.target.value))}
              >
                {settings.credits.stations.map((station) => (
                  <MenuItem key={station.station_id} value={station.station_id}>
                    {station.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Amount (+/-)"
              type="number"
              value={topupAmount}
              onChange={(event) => setTopupAmount(event.target.value)}
              fullWidth
            />
            <TextField
              label="Note (optional)"
              value={topupNote}
              onChange={(event) => setTopupNote(event.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={topupSaving ? <CircularProgress size={20} /> : <SaveRoundedIcon />}
              disabled={topupSaving}
              onClick={handleTopUp}
            >
              Apply
            </Button>
          </Stack>
          <Divider />
          <Typography variant="subtitle1">Recent usage</Typography>
          {creditsUsage && creditsUsage.ledger.length > 0 ? (
            <Stack spacing={1}>
              {creditsUsage.ledger.slice(0, 5).map((entry, index) => (
                <Typography key={`${entry.station_id}-${index}`} variant="body2">
                  {entry.created_at ?? ''} · {entry.station_name} · {entry.type} {entry.amount} ·{' '}
                  {entry.video_id || 'manual'}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No ledger entries recorded yet.
            </Typography>
          )}
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveRoundedIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Save all changes
        </Button>
      </Stack>
    </Stack>
  )
}

export default YtMp3SettingsPage
